import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { style } from 'typestyle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MedicalBagIcon from 'mdi-react/MedicalBagIcon';
import * as styles from './workplace-occupant-tooltip.styles';
import type { WorkplaceOccupant } from '../services/floorplan.service';
import type { WorkplaceOccupancy } from '../hooks/workplace-occupants.hooks';

interface Props {
  readonly occupancy: WorkplaceOccupancy;
  /**
   * Whether the occupants carry contact details, as the server reported it.
   *
   * False means the caller was not sent email addresses or phone numbers — a different thing from
   * a colleague who shared neither, and the reason the rows are gated on this rather than on the
   * fields being null.
   */
  readonly containsContactDetails: boolean;
  /** Pointer position inside the plan container, in CSS pixels. */
  readonly x: number;
  readonly y: number;
  readonly containerWidth: number;
  readonly containerHeight: number;
}

/** Until the card has been measured, enough room for one occupant. */
const ASSUMED_HEIGHT = 140;

/**
 * How many people one date lists before the rest become a count.
 *
 * A group reservation lists every member against every desk it holds, so the card is as long as
 * the team. It cannot scroll — the pointer leaving it closes it — and the plan container clips it,
 * so past this the names were simply invisible. The full list is in the desk's reservations
 * dialog; this card is a hover, not a report.
 */
const MAX_OCCUPANTS_SHOWN = 6;

/** Never flush against the container edge. */
const EDGE_MARGIN = 8;

const displayName = (occupant: WorkplaceOccupant): string =>
  [occupant.firstName, occupant.lastName].filter(Boolean).join(' ').trim();

/**
 * Two letters for the avatar circle, from whatever the row has.
 *
 * Falls back to the email because most employees have no photo and some have no name either —
 * a check-in with no agenda entry behind it carries neither.
 */
const initialsFor = (occupant: WorkplaceOccupant): string => {
  const name = displayName(occupant);
  if (name) {
    const parts = name.split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (occupant.email) {
    return occupant.email.substring(0, 2).toUpperCase();
  }
  return '?';
};

/**
 * Who has this desk, shown while the pointer is on it.
 *
 * <p>
 * A booked desk on the check-in floorplan was a red dot and nothing else: an administrator
 * seating somebody could see that a seat was taken but not by whom, which is the question they
 * are actually asking — is this the colleague who can be moved, or the one who cannot.
 * </p>
 *
 * <p>
 * Positioned in HTML above the SVG rather than drawn into it, so the card keeps its size and
 * its type at any zoom, and opens on whichever side of the pointer has room for it.
 * </p>
 */
const WorkplaceOccupantTooltip = ({
  occupancy,
  containsContactDetails,
  x,
  y,
  containerWidth,
  containerHeight,
}: Props): React.JSX.Element => {
  const { t, i18n } = useTranslation();
  const card = useRef<HTMLDivElement | null>(null);
  // Identifies the content the measurement belongs to. Without it the card carried the previous
  // desk's height into its first frame on the next desk, and a one-person card opening at the
  // bottom edge of the plan was placed as though it were a group's.
  const contentKey = `${occupancy.workplaceId}:${occupancy.dates.length}`;
  const [measurement, setMeasurement] = useState<{ key: string; height: number }>({
    key: contentKey,
    height: ASSUMED_HEIGHT,
  });

  // Measured rather than assumed, because the card is one line tall for a single booking and
  // several times that for a group reservation — and it is the height that decides whether it
  // opens above or below the pointer.
  useLayoutEffect(() => {
    const measured = card.current?.offsetHeight;
    if (measured == null) return;
    if (measurement.key === contentKey && measurement.height === measured) return;

    setMeasurement({ key: contentKey, height: measured });
  }, [contentKey, measurement]);

  const measured = measurement.key === contentKey;
  const height = measured ? measurement.height : ASSUMED_HEIGHT;

  const opensLeft = x + styles.POINTER_OFFSET + styles.WIDTH > containerWidth - EDGE_MARGIN;
  const left = opensLeft
    ? Math.max(EDGE_MARGIN, x - styles.POINTER_OFFSET - styles.WIDTH)
    : x + styles.POINTER_OFFSET;

  const opensAbove = y + styles.POINTER_OFFSET + height > containerHeight - EDGE_MARGIN;
  const top = opensAbove
    ? Math.max(EDGE_MARGIN, y - styles.POINTER_OFFSET - height)
    : y + styles.POINTER_OFFSET;

  const sourceLabel = (occupant: WorkplaceOccupant): string | undefined => {
    switch (occupant.source) {
      case 'GroupReservation':
        return t('workplace-occupant-source-group') ?? undefined;
      case 'CheckIn':
        return t('workplace-occupant-source-checkin') ?? undefined;
      default:
        // Their own booking of this desk is the ordinary case; labelling it would be noise.
        return undefined;
    }
  };

  const renderOccupant = (occupant: WorkplaceOccupant, index: number): React.JSX.Element => {
    const name = displayName(occupant);
    const badge = sourceLabel(occupant);

    return (
      <div
        key={`${occupant.email ?? 'unknown'}-${index}`}
        className={style(index === 0 ? styles.occupantRow : styles.occupantRowDivided)}
      >
        {occupant.avatarUrl ? (
          <img className={style(styles.avatar)} src={occupant.avatarUrl} alt={name || occupant.email || ''} />
        ) : (
          <span className={style(styles.initials)} aria-hidden="true">{initialsFor(occupant)}</span>
        )}
        <span className={style(styles.details)}>
          {name ? (
            <span className={style(styles.name)}>{name}</span>
          ) : (
            <span className={style(styles.unknownOccupant)}>{t('workplace-occupant-unknown')}</span>
          )}
          {/* Not gated on containsContactDetails: the server sends this flag to every viewer,
              because who to fetch when somebody is hurt is not a contact detail. */}
          {occupant.isCompanyMedic === true && (
            <span className={style(styles.medicLine)}>
              <MedicalBagIcon size={14} aria-hidden="true" />
              {t('company-medic')}
            </span>
          )}
          {containsContactDetails && occupant.email && (
            <span className={style(styles.contactLine)}>
              <MailOutlineIcon className={style(styles.contactIcon)} fontSize="inherit" />
              {occupant.email}
            </span>
          )}
          {containsContactDetails && occupant.phoneNumber && (
            <span className={style(styles.contactLine)}>
              <PhoneIphoneIcon className={style(styles.contactIcon)} fontSize="inherit" />
              {occupant.phoneNumber}
            </span>
          )}
          {badge && <span className={style(styles.sourceBadge)}>{badge}</span>}
        </span>
      </div>
    );
  };

  return (
    <div
      ref={card}
      role="tooltip"
      className={style(styles.card)}
      // Hidden for the single frame before it has been measured, so it cannot be seen at a
      // placement computed from the wrong height.
      style={{ position: 'absolute', left, top, zIndex: 3, visibility: measured ? 'visible' : 'hidden' }}
    >
      <div className={style(styles.header)}>
        <span>{t('workplace-occupant-tooltip-title')}</span>
        {occupancy.roomName && <span className={style(styles.roomName)}>{occupancy.roomName}</span>}
      </div>

      {occupancy.dates.map((day) => (
        <div key={day.date}>
          {/* Only worth a line when more than one day was asked for — the check-in screen books
              a range at once, and the same desk can be held by different people across it. */}
          {occupancy.dates.length > 1 && (
            <div className={style(styles.dateHeading)}>
              {new Date(day.date).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' })}
            </div>
          )}
          {day.occupants.slice(0, MAX_OCCUPANTS_SHOWN).map(renderOccupant)}
          {day.occupants.length > MAX_OCCUPANTS_SHOWN && (
            <div className={style(styles.overflowCount)}>
              {t('workplace-occupant-more').replace(
                'COUNT',
                String(day.occupants.length - MAX_OCCUPANTS_SHOWN),
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default WorkplaceOccupantTooltip;
